import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Table from '../../components/common/Table';
import Modal from '../../components/common/Modal';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';

interface GroupMember {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: string;
  joinedAt: string;
}

interface Group {
  id: string;
  name: string;
  description: string;
  groupType: 'project' | 'department' | 'team' | 'other';
  status: 'active' | 'inactive' | 'archived';
  memberCount: number;
  members?: GroupMember[];
  createdAt: string;
  updatedAt: string;
}

interface User {
  id: string;
  name: string;
  email: string;
}

const groupTypeLabels: Record<string, string> = {
  project: 'プロジェクト',
  department: '部署',
  team: 'チーム',
  other: 'その他',
};

const groupTypeColors: Record<string, string> = {
  project: 'bg-blue-500',
  department: 'bg-green-500',
  team: 'bg-purple-500',
  other: 'bg-gray-500',
};

const statusLabels: Record<string, string> = {
  active: '有効',
  inactive: '無効',
  archived: 'アーカイブ',
};

const initialFormData: {
  name: string;
  description: string;
  groupType: 'project' | 'department' | 'team' | 'other';
} = {
  name: '',
  description: '',
  groupType: 'team',
};

export default function GroupList() {
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);

  // メンバー管理
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const [removeMemberTarget, setRemoveMemberTarget] = useState<GroupMember | null>(null);

  // ページネーション・ソート・表示件数（URLから取得）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const setCurrentPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.delete('page'); return prev; });
  };
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'name', label: 'グループ名', type: 'text', placeholder: 'グループ名で検索' },
    { key: 'groupType', label: '種別', type: 'select', options: [
      { value: 'project', label: 'プロジェクト' },
      { value: 'department', label: '部署' },
      { value: 'team', label: 'チーム' },
      { value: 'other', label: 'その他' },
    ]},
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'active', label: '有効' },
      { value: 'inactive', label: '無効' },
      { value: 'archived', label: 'アーカイブ' },
    ]},
    { key: 'memberCount', label: 'メンバー数', type: 'number', placeholder: '人数' },
    { key: 'createdAt', label: '作成日', type: 'date' },
  ], []);

  useEffect(() => {
    fetchGroups();
  }, [searchConditions, currentPage, pageSize, sortConfig]);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);

      const data = await api.get<{ items: Group[]; total: number; totalPages: number }>(
        `/api/groups?${params.toString()}`
      );
      setGroups(Array.isArray(data?.items) ? data.items : []);
      setTotalItems(data?.total ?? 0);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setSearchParams(prev => { prev.delete('page'); return prev; });
  };

  const defaultSort = { key: 'createdAt', direction: 'desc' as const };
  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      let next: { key: string; direction: 'asc' | 'desc' };
      if (prev.key !== key) next = { key, direction: 'asc' };
      else if (prev.direction === 'asc') next = { key, direction: 'desc' };
      else next = defaultSort;
      setSearchParams(p => { p.set('sortBy', next.key); p.set('sortOrder', next.direction); p.delete('page'); return p; }, { replace: true });
      return next;
    });
  };

  const fetchMembers = async (groupId: string) => {
    setLoadingMembers(true);
    try {
      const data = await api.get<{ items: GroupMember[] }>(`/api/groups/${groupId}/members`);
      setMembers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const data = await api.get<{ items: User[] }>('/api/users?status=active');
      setAvailableUsers(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAvailableUsers([]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.post('/api/groups', formData);
      setShowCreateModal(false);
      setFormData(initialFormData);
      fetchGroups();
    } catch (error) {
      console.error('Failed to create group:', error);
      toast.error('グループの作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (group: Group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      groupType: group.groupType,
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedGroup || submitting) return;

    setSubmitting(true);
    try {
      await api.put(`/api/groups/${selectedGroup.id}`, formData);
      setShowEditModal(false);
      setSelectedGroup(null);
      setFormData(initialFormData);
      fetchGroups();
    } catch (error) {
      console.error('Failed to update group:', error);
      toast.error('グループの更新に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (group: Group) => {
    setDeleteTarget(group);
  };

  const confirmDeleteGroup = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/groups/${deleteTarget.id}`);
      fetchGroups();
      toast.success('グループを削除しました');
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast.error('グループの削除に失敗しました');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleStatus = async (group: Group) => {
    const newStatus = group.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/groups/${group.id}`, { status: newStatus });
      fetchGroups();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('ステータスの変更に失敗しました');
    }
  };

  const handleManageMembers = async (group: Group) => {
    setSelectedGroup(group);
    await Promise.all([fetchMembers(group.id), fetchAvailableUsers()]);
    setShowMemberModal(true);
  };

  const handleAddMember = async () => {
    if (!selectedGroup || !selectedUserId) return;

    try {
      await api.post(`/api/groups/${selectedGroup.id}/members`, {
        userId: selectedUserId,
        role: memberRole,
      });
      setSelectedUserId('');
      setMemberRole('member');
      fetchMembers(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      console.error('Failed to add member:', error);
      toast.error('メンバーの追加に失敗しました');
    }
  };

  const handleRemoveMember = (member: GroupMember) => {
    if (!selectedGroup) return;
    setRemoveMemberTarget(member);
  };

  const confirmRemoveMember = async () => {
    if (!selectedGroup || !removeMemberTarget) return;
    try {
      await api.delete(`/api/groups/${selectedGroup.id}/members/${removeMemberTarget.userId}`);
      fetchMembers(selectedGroup.id);
      fetchGroups();
      toast.success('メンバーを削除しました');
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('メンバーの削除に失敗しました');
    } finally {
      setRemoveMemberTarget(null);
    }
  };

  const handleChangeMemberRole = async (member: GroupMember, newRole: string) => {
    if (!selectedGroup) return;

    try {
      await api.put(`/api/groups/${selectedGroup.id}/members/${member.userId}`, {
        role: newRole,
      });
      fetchMembers(selectedGroup.id);
    } catch (error) {
      console.error('Failed to update member role:', error);
      toast.error('役割の変更に失敗しました');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'default' | 'warning'> = {
      active: 'success',
      inactive: 'default',
      archived: 'warning',
    };
    return <Badge variant={variants[status] || 'default'}>{statusLabels[status] || status}</Badge>;
  };

  const columns = [
    {
      key: 'name',
      header: 'グループ名',
      minWidth: '200px',
      sortable: true,
      mobileTitle: true,
      render: (group: Group) => (
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 ${groupTypeColors[group.groupType]} rounded-lg flex items-center justify-center text-white font-bold text-sm`}>
            {group.name.charAt(0)}
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{group.name}</p>
            <p className="text-xs text-gray-500">{groupTypeLabels[group.groupType]}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'description',
      header: '説明',
      minWidth: '200px',
      hideOnMobile: true,
      render: (group: Group) => (
        <span className="line-clamp-2 text-gray-600 dark:text-gray-400">{group.description || '-'}</span>
      ),
    },
    {
      key: 'memberCount',
      header: 'メンバー',
      minWidth: '80px',
      sortable: true,
      render: (group: Group) => (
        <button
          onClick={() => handleManageMembers(group)}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          {group.memberCount}名
        </button>
      ),
    },
    {
      key: 'status',
      header: 'ステータス',
      minWidth: '80px',
      render: (group: Group) => getStatusBadge(group.status),
    },
    {
      key: 'actions',
      header: '',
      minWidth: '120px',
      render: (group: Group) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => handleManageMembers(group)} className="px-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="hidden lg:inline ml-1">メンバー</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(group)} className="px-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="hidden lg:inline ml-1">編集</span>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(group)} className="px-2 text-red-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span className="hidden lg:inline ml-1">削除</span>
          </Button>
        </div>
      ),
    },
  ];

  const renderForm = (onSubmit: (e: React.FormEvent) => void, isEdit = false) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <Input
        label="グループ名"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
        placeholder="グループ名を入力"
      />
      <Select label="種別" name="groupType" value={formData.groupType} onChange={handleChange}>
        <option value="project">プロジェクト</option>
        <option value="department">部署</option>
        <option value="team">チーム</option>
        <option value="other">その他</option>
      </Select>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          説明
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 text-gray-900 dark:text-white"
          rows={3}
          placeholder="グループの説明を入力"
        />
      </div>
      <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setFormData(initialFormData);
          }}
          className="w-full sm:w-auto"
        >
          キャンセル
        </Button>
        <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
          {submitting ? '処理中...' : isEdit ? '更新' : '作成'}
        </Button>
      </div>
    </form>
  );

  // 既存メンバー以外のユーザーをフィルタ
  const nonMembers = availableUsers.filter(
    (u) => !members.some((m) => m.userId === u.id)
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">グループ管理</h1>
        {canCreate('groups') && (
          <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            グループを作成
          </Button>
        )}
      </div>

      {/* テーブル */}
      <Card>
        {/* 詳細検索 */}
        <div className="mb-4">
          <AdvancedSearch
            fields={searchFields}
            onSearch={handleSearch}
            quickSearchPlaceholder="グループ名で検索..."
            quickSearchField="name"
            initialConditions={searchConditions}
          />
        </div>
        <Table 
          columns={columns} 
          data={groups} 
          loading={loading}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
        
        {/* ページネーション */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 order-2 sm:order-1">
            <span className="hidden sm:inline">表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-20 sm:w-24 text-sm"
            >
              <option value="10">10件</option>
              <option value="30">30件</option>
              <option value="50">50件</option>
              <option value="100">100件</option>
            </Select>
            <span className="text-gray-400">/ {totalItems}件</span>
          </div>
          <div className="order-1 sm:order-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </Card>

      {/* 作成モーダル */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormData(initialFormData);
        }}
        title="グループを作成"
      >
        {renderForm(handleCreate)}
      </Modal>

      {/* 編集モーダル */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedGroup(null);
          setFormData(initialFormData);
        }}
        title="グループを編集"
      >
        {renderForm(handleUpdate, true)}
      </Modal>

      {/* メンバー管理モーダル */}
      <Modal
        isOpen={showMemberModal}
        onClose={() => {
          setShowMemberModal(false);
          setSelectedGroup(null);
          setMembers([]);
          setSelectedUserId('');
        }}
        title={`${selectedGroup?.name || ''} - メンバー管理`}
        size="lg"
      >
        <div className="space-y-6">
          {/* メンバー追加 */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">メンバーを追加</h3>
            <div className="flex flex-col sm:flex-row gap-2">
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="flex-1"
              >
                <option value="">ユーザーを選択...</option>
                {nonMembers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </Select>
              <Select
                value={memberRole}
                onChange={(e) => setMemberRole(e.target.value)}
                className="sm:w-32"
              >
                <option value="member">メンバー</option>
                <option value="leader">リーダー</option>
                <option value="admin">管理者</option>
              </Select>
              <Button onClick={handleAddMember} disabled={!selectedUserId} className="sm:w-auto">
                追加
              </Button>
            </div>
          </div>

          {/* メンバー一覧 */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              メンバー一覧 ({members.length}名)
            </h3>
            {loadingMembers ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : members.length === 0 ? (
              <p className="text-center text-gray-500 py-8">メンバーがいません</p>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 border dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                        {member.userName.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">{member.userName}</p>
                        <p className="text-xs text-gray-500">{member.userEmail}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={member.role}
                        onChange={(e) => handleChangeMemberRole(member, e.target.value)}
                        className="w-28 text-sm"
                      >
                        <option value="member">メンバー</option>
                        <option value="leader">リーダー</option>
                        <option value="admin">管理者</option>
                      </Select>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(member)}>
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t dark:border-gray-700">
            <Button variant="secondary" onClick={() => setShowMemberModal(false)}>
              閉じる
            </Button>
          </div>
        </div>
      </Modal>

      {/* グループ削除確認モーダル */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">「{deleteTarget?.name}」を削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmDeleteGroup}>削除</Button>
        </div>
      </Modal>

      {/* メンバー削除確認モーダル */}
      <Modal isOpen={!!removeMemberTarget} onClose={() => setRemoveMemberTarget(null)} title="削除確認">
        <p className="text-gray-700 dark:text-gray-300">{removeMemberTarget?.userName} をグループから削除しますか？</p>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setRemoveMemberTarget(null)}>キャンセル</Button>
          <Button variant="danger" onClick={confirmRemoveMember}>削除</Button>
        </div>
      </Modal>
    </div>
  );
}
